import t from "@/lang/lang";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  DecimalNumberInputField,
  NumberInputField,
  TextInputField,
} from "@/shared/components/field/input-field";
import { EditorInputField } from "@/shared/components/field/editor-input-field";
import { PaymentTerms } from "../types/payment-terms";

/**
 * PaymentTermsCard edits the payment terms agreed with a customer. The
 * fields are keyed `<fieldName>.<field>`.
 */
export function PaymentTermsCard({
  paymentTerms,
  onPaymentTermsChange,
  fieldName = "paymentTerms",
  disabled,
}: {
  paymentTerms: PaymentTerms;
  onPaymentTermsChange: (paymentTerms: PaymentTerms) => void;
  fieldName?: string;
  disabled?: boolean;
}) {
  const change = (p: Partial<PaymentTerms>) =>
    onPaymentTermsChange({ ...paymentTerms, ...p });
  const key = (field: keyof PaymentTerms) => `${fieldName}.${field}`;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Zahlungsbedingungen")}</CardTitle>
        <CardDescription>
          {t("Wann Rechnungen gestellt werden und zu zahlen sind.")}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 lg:grid-cols-2">
        <TextInputField
          label={t("Kurzbezeichnung")}
          text={paymentTerms.shorthand}
          onTextChange={(shorthand) => change({ shorthand })}
          fieldName={key("shorthand")}
          fieldProps={{ className: "lg:col-span-2" }}
          disabled={disabled}
        />
        <NumberInputField
          label={t("Zahlungsziel (Tage)")}
          number={paymentTerms.paymentPeriod}
          onNumberChange={(paymentPeriod) => change({ paymentPeriod })}
          fieldName={key("paymentPeriod")}
          min={0}
          disabled={disabled}
        />
        <NumberInputField
          label={t("Rechnungsintervall (Tage)")}
          number={paymentTerms.subscriptionInvoiceInterval}
          onNumberChange={(subscriptionInvoiceInterval) =>
            change({ subscriptionInvoiceInterval })
          }
          fieldName={key("subscriptionInvoiceInterval")}
          min={0}
          disabled={disabled}
        />
        <DecimalNumberInputField
          label={t("Skonto (%)")}
          value={paymentTerms.cashDiscountPercent}
          onValueChange={(cashDiscountPercent) =>
            change({ cashDiscountPercent })
          }
          fieldName={key("cashDiscountPercent")}
          disabled={disabled}
        />
        <NumberInputField
          label={t("Skontofrist (Tage)")}
          number={paymentTerms.cashDiscountPaymentPeriod}
          onNumberChange={(cashDiscountPaymentPeriod) =>
            change({ cashDiscountPaymentPeriod })
          }
          fieldName={key("cashDiscountPaymentPeriod")}
          min={0}
          disabled={disabled}
        />
        <NumberInputField
          label={t("Vorabrechnung (Tage)")}
          number={paymentTerms.billedInAdvancePeriod}
          onNumberChange={(billedInAdvancePeriod) =>
            change({ billedInAdvancePeriod })
          }
          fieldName={key("billedInAdvancePeriod")}
          min={0}
          disabled={disabled}
        />
        <DecimalNumberInputField
          label={t("Rabatt bei Vorabrechnung (%)")}
          value={paymentTerms.billedInAdvanceDiscountPercent}
          onValueChange={(billedInAdvanceDiscountPercent) =>
            change({ billedInAdvanceDiscountPercent })
          }
          fieldName={key("billedInAdvanceDiscountPercent")}
          disabled={disabled}
        />
        <EditorInputField
          label={t("Text")}
          initialHtml={paymentTerms.text}
          onHtmlChange={(text) => change({ text })}
          fieldName={key("text")}
          fieldProps={{ className: "lg:col-span-2" }}
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
}
